import { useEffect, useRef, useState } from 'react'
import { useCart, flattenConnection } from '@shopify/hydrogen-react'
import { request } from 'graphql-request'
import { useAppStore } from 'lib/hooks/useAppStore'
import { shopClient } from 'lib/shopify.client'
import {
  gqlCollectionQuery,
  recommendedProductsQuery,
} from 'lib/sanity.queries'
import { AnimatePresence, motion } from 'motion/react'

import { ItemsTab } from './Items'
import { RecommendationsTab } from './Recommendations'
import { CloseButton } from 'components/Icons/CloseButton'

export const CartOverlay = ({ defaultCollection }) => {
  const { lines, status, totalQuantity } = useCart()
  const { setCartState, cartState } = useAppStore()
  const initialQuantity = useRef(totalQuantity)
  const [recommendedProducts, setRecommendedProducts] = useState([])
  const [tab, setTab] = useState<'items' | 'recommendations'>('items')
  const [itemsNotification, setItemsNotification] = useState(false)
  const [recommendationsNotification, setRecommendationsNotification] =
    useState(true)

  useEffect(() => {
    if (totalQuantity !== initialQuantity.current) {
      setItemsNotification(true)
    }
  }, [totalQuantity])

  useEffect(() => {
    if (tab === 'recommendations') {
      setRecommendationsNotification(false)
    } else if (tab === 'items') {
      setItemsNotification(false)
    }
  }, [tab, cartState])

  useEffect(() => {
    if (lines && lines[0]?.merchandise?.product?.id) {
      request({
        url: shopClient.getStorefrontApiUrl(),
        document: recommendedProductsQuery,
        variables: {
          id: lines[0]?.merchandise?.product?.id,
        },
        requestHeaders: shopClient.getPublicTokenHeaders(),
      })
        .then((res: any) => {
          if (res && res.productRecommendations) {
            setRecommendedProducts(res.productRecommendations)
          }
        })
        .catch((err) => console.error(err))
    } else {
      request({
        url: shopClient.getStorefrontApiUrl(),
        document: gqlCollectionQuery,
        variables: {
          slug: defaultCollection?.slug ?? 'best-sellers',
        },
        requestHeaders: shopClient.getPublicTokenHeaders(),
      }).then((res: any) => {
        if (res && res.collection?.products) {
          setRecommendedProducts(
            flattenConnection(res.collection?.products) as any
          )
        }
      })
    }
  }, [lines, defaultCollection])

  return (
    <motion.div
      className="h-screen-dynamic fixed inset-0 z-50 flex w-full justify-center px-14 pt-60 text-darkGray will-change-auto md:z-10 md:items-center md:px-20 md:pt-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: 'easeIn',
      }}
    >
      <motion.div
        className="absolute inset-0 z-0 bg-blackTransparent backdrop-blur-100 will-change-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        layout
        transition={{
          duration: 0.3,
          ease: 'easeIn',
        }}
      />
      <button
        className="absolute right-0 right-32 top-[80px] z-20 p-12 uppercase md:hidden"
        onClick={() => setCartState('closed')}
      >
        <CloseButton />
      </button>
      <div className="background-blur relative z-10 flex max-h-[542px] w-full max-w-[700px] flex-col overflow-hidden rounded-8 p-12 md:max-h-[75vh] md:p-20">
        <div className="relative flex w-full items-center justify-center gap-14 pb-[28px] pt-40 text-14  text-midGray md:gap-80 md:pb-38 md:pt-16 md:text-16">
          <button
            className={`ease relative transition-opacity duration-200 ${
              tab === 'items' ? 'opacity-100' : 'opacity-50'
            }`}
            onClick={() => setTab('items')}
          >
            Your Items
            {itemsNotification ? (
              <div className="absolute left-full top-0 ml-[3px] mt-[5px] h-[5px] w-[5px] rounded-full bg-yellow" />
            ) : null}
          </button>
          <button
            className={`ease relative transition-opacity duration-200 ${
              tab === 'recommendations' ? 'opacity-100' : 'opacity-50'
            }`}
            onClick={() => setTab('recommendations')}
          >
            Recommended products
            {recommendationsNotification ? (
              <div className="absolute left-full top-0 ml-[3px] mt-[5px] h-[5px] w-[5px] rounded-full bg-yellow" />
            ) : null}
          </button>
          <button
            className="right-0 top-0 hidden p-12 uppercase md:absolute md:top-[unset] md:block"
            onClick={() => setCartState('closed')}
          >
            <CloseButton />
          </button>
        </div>
        <AnimatePresence mode="wait">
          {tab === 'items' ? (
            <ItemsTab key="items" lines={lines} status={status} />
          ) : null}
          {tab === 'recommendations' ? (
            <RecommendationsTab
              key="recommendations"
              products={recommendedProducts}
            />
          ) : null}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}
